import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { useAuthStore } from '../../stores/auth.store'
import { getInitials } from '../../lib/utils'
import { api } from '../../lib/api'

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard':   'Dashboard',
  '/schedule':    'Schedule',
  '/curriculum':  'Curriculum',
  '/lessons':     'Lesson Logs',
  '/homework':    'Homework',
  '/assessments': 'Assessments',
  '/children':    'Children',
  '/reports':     'Reports',
  '/merits':      'Merits & Rewards',
  '/ai':          'AI Intelligence',
  '/settings':    'Settings',
}

const NOTIF_ICONS: Record<string, string> = {
  LESSON_REMINDER:    '📅',
  LESSON_LOGGED:      '✅',
  MISSED_LESSON:      '⚠️',
  HOMEWORK_ASSIGNED:  '📚',
  HOMEWORK_SUBMITTED: '✏️',
  HOMEWORK_MARKED:    '📊',
  ASSESSMENT_SCORED:  '📝',
  REPORT_READY:       '📄',
  AI_REPORT_READY:    '🤖',
  MILESTONE_ACHIEVED: '🏆',
  MERIT_AWARDED:      '⭐',
  WEEKLY_SUMMARY:     '📋',
  CALENDAR_SYNC:      '📆',
  PDF_READY:          '📄',
}

type Notification = {
  id: string
  type: string
  title: string
  body: string
  isRead: boolean
  data: Record<string, unknown> | null
  createdAt: string
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10 2a6 6 0 0 0-6 6v2.586l-.707.707A1 1 0 0 0 4 13h12a1 1 0 0 0 .707-1.707L16 10.586V8a6 6 0 0 0-6-6z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 18a2 2 0 0 0 2-2H8a2 2 0 0 0 2 2z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export default function Header({ onMenuClick }: { onMenuClick?: () => void }) {
  const { user, logout } = useAuthStore()
  const navigate  = useNavigate()
  const { pathname } = useLocation()
  const qc        = useQueryClient()
  const [showNotifs, setShowNotifs] = useState(false)
  const bellRef   = useRef<HTMLDivElement>(null)

  const title = ROUTE_TITLES[pathname] ?? 'Sprout Hill School'

  const { data: notifData } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const r = await api.get('/notifications?limit=15')
      return r.data as { data: Notification[]; meta: { unreadCount: number } }
    },
    refetchInterval: 30000,
  })

  const notifications: Notification[] = notifData?.data ?? []
  const unreadCount: number = notifData?.meta?.unreadCount ?? 0

  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const markAllRead = useMutation({
    mutationFn: () => api.patch('/notifications/mark-all-read'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  const dismissNotif = useMutation({
    mutationFn: (id: string) => api.delete(`/notifications/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  // Close panel when clicking outside
  useEffect(() => {
    if (!showNotifs) return
    const handler = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setShowNotifs(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showNotifs])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  function notifNavPath(n: Notification): string | null {
    const d = n.data ?? {}
    switch (n.type) {
      case 'LESSON_REMINDER':
      case 'LESSON_LOGGED':
      case 'MISSED_LESSON':
        return d['lessonId'] ? `/lessons/${d['lessonId']}` : '/dashboard'
      case 'HOMEWORK_SUBMITTED':
      case 'HOMEWORK_MARKED':
        return '/homework'
      case 'ASSESSMENT_SCORED':
        return '/assessments'
      case 'REPORT_READY':
      case 'PDF_READY':
        return '/reports'
      case 'AI_REPORT_READY':
        return '/ai'
      case 'MILESTONE_ACHIEVED':
        return '/reports'
      case 'MERIT_AWARDED':
        return '/merits'
      case 'WEEKLY_SUMMARY':
        return '/dashboard'
      default:
        return null
    }
  }

  return (
    <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-4 sm:px-6 fixed top-0 right-0 left-0 lg:left-64 z-10">
      <div className="flex items-center gap-3">
        {/* Hamburger — mobile only */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 flex-shrink-0"
          aria-label="Open menu"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round" />
          </svg>
        </button>
        <h1 className="text-base sm:text-lg font-semibold text-[#2C3E50] truncate">{title}</h1>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {/* Notification bell */}
        <div ref={bellRef} className="relative">
          <button
            onClick={() => setShowNotifs((v) => !v)}
            className={`relative p-2 rounded-lg transition-colors ${showNotifs ? 'bg-gray-100 text-[#1B6B3A]' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
            aria-label="Notifications"
          >
            <BellIcon />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Dropdown panel */}
          {showNotifs && (
            <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-[#2C3E50]">
                  Notifications
                  {unreadCount > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded-full font-medium">
                      {unreadCount} new
                    </span>
                  )}
                </p>
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllRead.mutate()}
                    disabled={markAllRead.isPending}
                    className="text-xs text-[#1B6B3A] hover:underline disabled:opacity-50"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {/* List */}
              <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                {notifications.length === 0 && (
                  <p className="text-center text-sm text-gray-400 py-8">No notifications yet.</p>
                )}
                {notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`flex gap-3 px-4 py-3 group/notif hover:bg-gray-50 transition-colors ${!n.isRead ? 'bg-blue-50/50' : ''}`}
                  >
                    <div
                      className="flex gap-3 flex-1 cursor-pointer"
                      onClick={() => {
                        if (!n.isRead) markRead.mutate(n.id)
                        const path = notifNavPath(n)
                        if (path) { setShowNotifs(false); navigate(path) }
                      }}
                    >
                    <span className="text-lg flex-shrink-0 mt-0.5">
                      {NOTIF_ICONS[n.type] ?? '🔔'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-snug ${!n.isRead ? 'font-semibold text-gray-800' : 'text-gray-700'}`}>
                          {n.title}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0 mt-1">
                          {!n.isRead && <span className="w-2 h-2 rounded-full bg-blue-500" />}
                          {notifNavPath(n) && <span className="text-gray-300 text-xs">→</span>}
                        </div>
                      </div>
                      {n.body && (
                        <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">{n.body}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {format(parseISO(n.createdAt), 'd MMM · h:mm a')}
                      </p>
                    </div>
                    </div>
                    {/* Dismiss button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); dismissNotif.mutate(n.id) }}
                      className="flex-shrink-0 self-start mt-1 text-gray-200 hover:text-gray-500 opacity-0 group-hover/notif:opacity-100 transition-opacity text-sm leading-none"
                      title="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Avatar + name */}
        <div className="flex items-center gap-2">
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#1B6B3A] flex items-center justify-center text-white text-xs font-semibold">
              {getInitials(user?.firstName ?? '', user?.lastName ?? '')}
            </div>
          )}
          <span className="text-sm text-gray-700 hidden sm:block">
            {user?.firstName} {user?.lastName}
          </span>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="text-xs text-gray-500 hover:text-red-600 transition-colors px-2 py-1 rounded hover:bg-red-50"
        >
          Sign out
        </button>
      </div>
    </header>
  )
}
